<template>
  <div class="search-bar__wrapper">
    <div v-show="showingItems">
      <div class="d-flex align-center">
        <!-- 地區 -->
        <v-menu v-model="regionMenu" min-width="264" max-height="308" offset="16" slide-x>
          <template #activator="{ props }">
            <p class="wd-font-normal !wd-mb-0">
              <v-btn class="filter-btn" height="40" elevation="0" variant="text" v-bind="props">
                <funnow-icon class="mr-2" max-width="24" :image-path="'location-icon_black.svg'" />
                {{ localSelectedRegion?.name || '' }}
              </v-btn>
            </p>
          </template>

          <v-list v-if="localSelectedRegion" :value="localSelectedRegion.id" color="orange50" mandatory>
            <v-list-item
              v-for="regionOption in allRegions"
              :key="regionOption.region_id"
              :value="regionOption.region_id"
              color="orange50"
              @click="handleRedirectToRegion(regionOption)"
            >
              <v-list-item-title> {{ regionOption.name }} </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <span v-if="!showRegionOnly" class="vertical-divider" />

        <!-- 項目 -->
        <v-menu v-if="!showRegionOnly" v-model="subCategoryMenu" min-width="264" max-height="308" offset="16" slide-x>
          <template #activator="{ props }">
            <component :is="isNoH1Tag ? 'p' : 'h1'" class="wd-font-normal wd-flex wd-items-start !wd-mb-0">
              <v-btn class="filter-btn" height="40" elevation="0" variant="text" v-bind="props">
                <funnow-icon class="mr-2" max-width="24" :image-path="'funnow-icon_black.svg'" />
                {{ firstLevelCategoryText }}
              </v-btn>
            </component>
          </template>

          <v-list :value="firstLevelCategoryId" color="orange50" mandatory>
            <v-list-item
              v-for="(category, idx) in categoryOrThemeList"
              :key="idx"
              :value="category.id"
              color="orange50"
              @click="redircetToCategory(category.id)"
            >
              <v-list-item-title> {{ category.name }} </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-spacer v-if="!showRegionOnly" />

        <!-- 搜尋 -->
        <v-form
          v-show="isSearchInputShow && !isNavigation && !showRegionOnly && !isEmbeddedSearchBarVisible"
          class="search-form"
          autocomplete="off"
          @submit.prevent="doSearch({ isEnter: true })"
        >
          <v-menu
            v-model="isSearchMenuShow"
            :close-on-content-click="false"
            :disabled="searchKeywordsOfCategory.length === 0 && historyKeyWords.length === 0"
            min-width="728"
            offset="16"
            slide-x
            z-index="10"
          >
            <template #activator="{ props }">
              <v-text-field
                v-model="inputKeyword"
                class="search-input wd-h-11 wd-w-full"
                v-bind="props"
                :placeholder="searchPlaceholder"
                type="search"
                color="orange50"
                autofocus
                variant="outlined"
                density="compact"
                hide-details
                @blur="blurKeywordInput"
              >
                <template #prepend-inner>
                  <funnow-icon class="mr-3 wd-w-5 wd-h-5" max-width="20" :image-path="'magnify-icon_black.svg'" />
                </template>

                <template #append-inner>
                  <v-btn class="clear-btn pa-1" min-width="24" width="24" height="24" elevation="0" variant="text" @click="clearSearchKeyword">
                    <funnow-icon max-width="16" :image-path="'remove-icon_gray.svg'" />
                  </v-btn>
                </template>
              </v-text-field>
            </template>

            <div>
              <search-content-card
                ref="searchContentCard"
                :popular-keywords="searchKeywordsOfCategory"
                :history-key-words="historyKeyWords"
                @change-keyword="onKeywordClicked"
              />
            </div>
          </v-menu>
        </v-form>

        <!-- 搜尋按鈕 -->
        <v-btn
          v-show="!(isSearchInputShow && !isNavigation) && !showRegionOnly && !isEmbeddedSearchBarVisible"
          class="search__btn mr-2"
          min-width="40"
          width="40"
          height="40"
          elevation="0"
          variant="text"
          @click="showSearchInput"
        >
          <funnow-icon class="wd-w-6 wd-h-6" max-width="24" :image-path="'magnify-icon_black.svg'" />
        </v-btn>
      </div>
    </div>

    <!-- 側邊搜尋 -->
    <SearchNavigation
      v-if="isNavigation"
      :search-keyword="searchKeyword"
      :placeholder="searchPlaceholder"
      :popular-keywords="searchKeywordsOfCategory"
      :history-key-words="historyKeyWords"
      @submit-keyword="handleMobileKeywordSubmit"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { isEmpty as _isEmpty } from 'lodash-es'
import { useVuetify } from '@/composition/useUtil'
import { useGtm, useGtmSearchQuerySent } from '@/composition/useGtm'
import { GtmEventMap } from '@/view-models/gtm'

const SearchNavigation = defineAsyncComponent(() => import('@/components/search/search-navigation.vue'))
const SearchContentCard = defineAsyncComponent(() => import('@/components/search/search-content-card.vue'))
const FunnowIcon = defineAsyncComponent(() => import('@/components/icons/funnow-icon.vue'))

export default defineComponent({
  name: 'SearchBar',
  components: {
    SearchNavigation,
    SearchContentCard,
    FunnowIcon,
  },
  props: {
    isNavigation: { type: Boolean, default: false },
    showingItems: { type: Boolean, default: true },
    showRegionOnly: { type: Boolean, default: false },
  },
  setup() {
    const { sendGtmEvent } = useGtm()
    const { keywordSource, fireSearchQuerySentEvent } = useGtmSearchQuerySent()
    const route = useRoute()
    const localePath = useLocalePath()
    const vuetify = useVuetify()
    const nuxtApp = useNuxtApp()

    const { $store: store } = nuxtApp

    const region = computed(() => store.getters['region/region'])
    const selectedRegion = computed(() => store.getters['search/selectedRegion'])
    const localSelectedRegion = computed(() => {
      if (selectedRegion.value) {
        return { ...selectedRegion.value }
      }

      if (region.value) {
        return {
          id: region.value.id,
          name: region.value.name,
        }
      }

      return null
    })

    const isDesktop = computed(() => vuetify.display.mdAndUp.value)
    const isCategoriesPage = computed(() => route.name.includes('regions-rid-categories-id'))
    const isThemesPage = computed(() => route.name.includes('regions-rid-themes-id'))
    const isSearchPage = computed(() => route.name.includes('regions-rid-search'))
    const isTagPage = computed(() => checkIfRegionTagPage(route.name))
    const hasNoCategoryScope = computed(() => isSearchPage.value || isTagPage.value)
    const isNoH1Tag = computed(() => isDesktop.value && (isCategoriesPage.value || isThemesPage.value))

    return {
      sendGtmEvent,
      keywordSource,
      fireSearchQuerySentEvent,
      isNoH1Tag,
      isCategoriesPage,
      isThemesPage,
      isSearchPage,
      isTagPage,
      hasNoCategoryScope,
      localSelectedRegion,
      region,
      localePath,
    }
  },
  data() {
    return {
      inputKeyword: '',
      isSearchInputShow: false,
      isSearchMenuShow: false,
      regionMenu: false,
      subCategoryMenu: false,
    }
  },
  computed: {
    loggedInUser() {
      return this.$auth.user
    },
    hasCategory() {
      return !_isEmpty(this.firstLevelCategory)
    },
    firstLevelCategoryId() {
      return this.hasCategory ? (this.hasNoCategoryScope ? null : this.firstLevelCategory.id) : null
    },
    nowCategory() {
      return this.category ? (this.hasNoCategoryScope ? null : this.category.id) : null
    },
    firstLevelCategoryText() {
      return this.hasCategory ? this.firstLevelCategory.name : this.$t('filter_category')
    },
    categoryOrThemeList() {
      if (this.isCategoriesPage || this.isSearchPage || this.isTagPage) {
        return this.categories
      } else if (this.isThemesPage) {
        return this.themes
      }
      return []
    },
    searchPlaceholder() {
      return this.$t('search_category_placeholder', { region: this.region.name, category: this.firstLevelCategoryText })
    },
    ...mapGetters({
      allRegions: 'region/allRegions',
      regionsMap: 'region/regionsMap',
      categories: 'region/categories',
      themes: 'region/themes',
      category: 'category/category',
      firstLevelCategory: 'category/firstLevelCategory',
      searchKeyword: 'search/searchKeyword',
      historyKeyWords: 'search/searchKeywordsOfHistory',
      searchKeywordsOfCategory: 'search/searchKeywordsOfCategory',
      shouldResetFilterStateAndKeyword: 'filter/shouldResetFilterStateAndKeyword',
      isEmbeddedSearchBarVisible: 'isEmbeddedSearchBarVisible',
    }),
  },
  watch: {
    category(newCategory) {
      if (newCategory) {
        this.fetchSearchKeywordsOfCategory()
      }
    },
    searchKeyword(newKeyword) {
      this.inputKeyword = newKeyword || ''
      if (newKeyword) {
        this.isSearchInputShow = true
      }
    },
    shouldResetFilterStateAndKeyword(newValue, prevValue) {
      if (newValue) {
        this.resetFilterData()
      }
    },
  },
  mounted() {
    this.initSearchBar()
    this.isSearchInputShow = !!this.searchKeyword
    this.fetchSearchKeywordsOfCategory()
  },
  methods: {
    initSearchBar() {
      if (this.searchKeyword) {
        this.inputKeyword = this.searchKeyword || ''
      }
    },
    fetchSearchKeywordsOfCategory() {
      const querys = {}
      if (this.hasNoCategoryScope) {
        querys.regionID = this.region.id
      } else {
        querys.categoryID = this.firstLevelCategoryId
      }
      this.$store.dispatch('search/fetchSearchKeywordsOfCategory', querys)
    },
    showSearchInput() {
      if (this.isNavigation) {
        this.$store.dispatch('setSearchNavigation', true)
      } else {
        this.isSearchInputShow = true
        this.$nextTick(() => {
          this.isSearchMenuShow = true
        })
      }
    },
    blurKeywordInput() {
      setTimeout(() => {
        if (!this.isSearchMenuShow && !this.inputKeyword) {
          this.isSearchInputShow = false
        }
      }, 100)
    },
    onKeywordClicked({ keyword, keywordSource }) {
      this.isSearchMenuShow = false
      this.inputKeyword = keyword
      this.keywordSource = keywordSource || ''
      this.doSearch()
    },
    clearSearchKeyword() {
      this.inputKeyword = ''
      this.doSearch()
    },
    buildSearchEventOptions({ isEnter = false } = {}) {
      return {
        keyword: this.inputKeyword,
        isEnter,
        sourceName: this.firstLevelCategory?.name || '',
        isListPage: this.isCategoriesPage || this.isThemesPage,
        isEmbeddedSearchBarVisible: this.isEmbeddedSearchBarVisible,
      }
    },
    handleMobileKeywordSubmit({ keyword: searchKeyword, keywordSource, isEnter }) {
      this.inputKeyword = searchKeyword
      this.keywordSource = keywordSource || ''
      this.$store.dispatch('search/setSearchKeyword', searchKeyword)
      this.fireSearchQuerySentEvent(this.buildSearchEventOptions({ isEnter }))
    },
    doSearch(options = {}) {
      const { isEnter = false } = options
      if (this.inputKeyword) {
        this.$store.dispatch('search/saveHistorySearchKeyword', this.inputKeyword)
      }
      this.fireSearchQuerySentEvent(this.buildSearchEventOptions({ isEnter }))
      this.$store.dispatch('search/setSearchKeyword', this.inputKeyword)
      this.$store.dispatch('setSearchNavigation', false)
    },
    resetFilterData() {
      this.clearSearchKeyword()
      this.blurKeywordInput()
      this.$store.dispatch('filter/resetFilterState')
    },
    async handleRedirectToRegion(region) {
      const regionId = region.region_id
      const event = {
        event: GtmEventMap.REGION_CHANGED,
        payload: {
          'To Region ID': regionId,
          'To Region Name': region.name,
        },
      }

      this.sendGtmEvent(event, false)
      this.$store.dispatch('search/setSelectedRegion', { id: regionId, name: region.name })
      this.resetFilterData()

      if (this.loggedInUser) {
        this.sendGtmEvent(event, true)
        await this.$store.dispatch('authentication/updateMemberRegion', { regionId, userInfo: this.loggedInUser })
      }

      this.$router.push(this.localePath({ name: 'regions-rid', params: { rid: regionId } }))
    },
    redircetToCategory(categoryID) {
      this.resetFilterData()
      this.$store.dispatch('search/setSelectedCategoryId', categoryID)
    },
  },
})
</script>

<style lang="scss" scoped>
.search-bar__wrapper {
  width: 100%;

  .vertical-divider {
    height: 24px;
    border-right: 1px solid $gray50;
  }

  .search-form {
    margin-left: 44px;
    width: 100%;
    .search-input {
      width: 100%;
      .clear-btn {
        margin-top: 2px;
      }
    }
  }

  .filter-btn {
    font-size: 17px;
  }
}
</style>
